import { useI18n } from "../../../i18n/i18nService";
import DataTable from "../../../components/common/DataTable";
import { useIdentification } from "../../../contexts/IdentificationContext";
import { ButtonCustom, FormError, Modal, TextAreaField, InputField } from "../../../components/common/Form";
import { useEffect, useState } from "react";


const TypeRaceFormModal = ({ open, onClose, item, onSubmit, isLoading }) => {
    const { t } = useI18n("identification");
    const isEditMode = !!item;
    const [formData, setFormData] = useState({
        libelle: '',
        description: ''
    });
    const [error, setError] = useState(null);

    // Remplir le formulaire en mode édition
    useEffect(() => {
        if (isEditMode && item) {
            setFormData({
                libelle: item.libelle || '',
                description: item.description || '',
            });
        } else {
            setFormData({ libelle: '', description: '' });
        }
    }, [item]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((data) => ({ ...data, [name]: value }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await onSubmit(formData, isEditMode);
            onClose();
        } catch (err) {
            setError(t(`identification:administration.type_races.error_codes.${err.status}`))
        }
    }

    return (
        <Modal open={open} onClose={onClose} title={isEditMode ? t('identification:administration.type_races.edit') : t('identification:administration.type_races.add')}>
            <form onSubmit={handleSubmit} className="space-y-4">
                {error && <FormError message={error || 'Unknow error'} />}
                <InputField
                    name="libelle"
                    label={t('identification:administration.type_races.name')}
                    value={formData.libelle}
                    onChange={handleChange}
                    required
                />
                <TextAreaField
                    name="description"
                    label={t('identification:administration.type_races.description')}
                    value={formData.description}
                    onChange={handleChange}
                />
                <div className="flex justify-end space-x-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
                        {t('cancel')}
                    </button>
                    <ButtonCustom type="submit" variant='primary' className="px-4 py-2" loading={isLoading}>
                        {t('save')}
                    </ButtonCustom>
                </div>
            </form>
        </Modal>
    )
}

const TypeRace = () => {
    const { t } = useI18n("identification");
    const {
        resources,
        loading,
        error,
        setError,
        // fetchResourceData,
        addResourceData,
        updateResourceData,
        deleteResourceData,
    } = useIdentification();

    const [openModal, setOpenModal] = useState(false);
    const [activeItem, setActiveItem] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const typeRaces = resources['type-race'] || [];

    const handleAdd = () => {
        setActiveItem(null); // Mode ajout
        setError(prev => ({ ...prev, 'type-race': null }))
        setOpenModal(true);
    };

    const handleEdit = (item) => {
        setActiveItem(item); // Mode édition
        setError(prev => ({ ...prev, 'type-race': null }))
        setOpenModal(true);
    };

    const handleDelete = (item) => {
        setActiveItem(item);
        setError(prev => ({ ...prev, 'type-race': null }))
        setConfirmDeleteOpen(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setActiveItem(null);
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
    };
    const handleFormSubmit = async (formData, isEditMode) => {
        if (isEditMode) {
            // console.log(activeItem)
            await updateResourceData('type-race', activeItem.id, { ...formData });
        } else {
            await addResourceData('type-race', formData);
        }
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteResourceData('type-race', activeItem.id);
            setConfirmDeleteOpen(false);
            setActiveItem(null);
        } catch (error) {
            setError(prev => ({ ...prev, typeRaces: t(`identification:administration.type_races.error_codes.${error.status}`) }))
        }

    }

    const columns = [
        {
            label: 'name',
            key: 'libelle',
            headerName: t('identification:administration.type_races.name'),
            width: 200,
            render: (row) => (
                <span className="text-sm font-medium">{row.translations && row.translations[0]?.libelle || row.libelle}</span>
            )
        },
        {
            label: 'description',
            key: 'description',
            headerName: t('identification:administration.type_races.description'),
            width: 300,
            render: (row) => (
                <span className="text-sm font-normal">{row.translations && row.translations[0]?.description || row.description || '-'}</span>
            )
        },
        {
            label: 'actions',
            headerName: t('actions'),
            key: 'actions',
            width: 150,
            render: (params) => (
                <div className="flex space-x-2">
                    <button className="text-blue-500 hover:underline" onClick={() => handleEdit(params)}>
                        {t('edit')}
                    </button>
                    <button className="text-red-500 hover:underline" onClick={() => handleDelete(params)}>
                        {t('delete')}
                    </button>
                </div>
            )
        }
    ]

    return (
        <div>
            <DataTable title={t('identification:administration.type_races.title')} columns={columns} data={typeRaces} isLoading={loading.fetching['type-race']} error={error['type-race']}>
                {/* Vous pouvez ajouter ici des actions supplémentaires ou des composants spécifiques */}
                <div className="flex justify-end mb-4">
                    <button onClick={handleAdd} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
                        {t('identification:administration.type_races.add')}
                    </button>
                </div>
            </DataTable>
            {openModal && (
                <TypeRaceFormModal
                    open={openModal}
                    onClose={handleCloseModal}
                    item={activeItem} // null pour ajout, objet pour modification
                    onSubmit={(formData, isEditMode) => handleFormSubmit(formData, isEditMode)}
                    isLoading={loading.mutation['type-race']}
                />
            )}
            {confirmDeleteOpen && (
                <Modal open={confirmDeleteOpen} onClose={handleCancelDelete} title={t('identification:administration.type_races.confirm_delete_title')}>
                    <div className="space-y-4">
                        {error.especes ?
                            <FormError message={error['type-race']} /> :
                            <p className='text-light-text dark:text-dark-text font-normal text-sm'>{t('identification:administration.type_races.confirm_delete_message')}</p>
                        }
                        <div className="flex justify-end space-x-2">
                            <button onClick={handleCancelDelete} className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
                                {t('cancel')}
                            </button>
                            <ButtonCustom onClick={handleConfirmDelete} variant="danger" className="px-4 py-2" loading={loading.mutation['type-race']}>
                                {t('confirm')}
                            </ButtonCustom>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    )
}

export default TypeRace;